/**
 * FHIR constants and query helpers for the Unify messaging feature.
 *
 * Thread model (Communication-based):
 * - Thread root = Communication with identifier 'conversation-topic'. Contains all participants
 *   in `recipient` for queryability, category for thread type, topic.text for group names.
 * - Message = Communication with partOf → thread root. Carries sender, payload, sent.
 * - Sub-thread reply = Communication with partOf → parent message + inResponseTo → parent message.
 *   This keeps sub-thread queries simple (part-of:parentMsg) while preserving FHIR reply semantics.
 * - Attachments use native payload.contentAttachment.
 * - Read/unread: `readByUser` extension (JSON map ref → ISO time) on thread roots. Each send bumps the
 *   sender’s entry to `thread.sent` so senders aren’t unread for their own messages; others stay unread
 *   until their `readByUser` time ≥ `thread.sent` (after opening the thread).
 *   Sub-thread replies do NOT touch the root — they don't affect main-chat unread state or sidebar preview.
 */

import type { MedplumClient } from '@medplum/core';
import type {
  Communication,
  Group as FhirGroup,
  Reference,
} from '@medplum/fhirtypes';

/** Narrow Reference to the union Communication.sender accepts. */
type SenderRef = NonNullable<Communication['sender']>;
type RecipientRef = NonNullable<NonNullable<Communication['recipient']>[number]>;

// ── Constants ────────────────────────────────────────────────────────────────

export const MSG = {
  IDENTIFIER_SYSTEM: 'https://unify.health/fhir/identifier/messaging',
  THREAD_IDENTIFIER_VALUE: 'conversation-topic',
  MESSAGE_IDENTIFIER_VALUE: 'message',
  CATEGORY_SYSTEM: 'https://unify.health/fhir/CodeSystem/messaging-category',
  EXT: {
    UNREAD_COUNT: 'https://unify.health/fhir/messaging/unread-count',
    READ_AT: 'https://unify.health/fhir/messaging/read-at',
    /** JSON object: { "Patient/abc": "2026-01-01T00:00:00.000Z", "Practitioner/xyz": "..." } */
    READ_BY_USER: 'https://unify.health/fhir/messaging/read-by-user',
    SEQUENCE_NUMBER: 'https://unify.health/fhir/messaging/sequence-number',
    DELIVERED_AT: 'https://unify.health/fhir/messaging/delivered-at',
    QUOTED_MESSAGE_ID: 'https://unify.health/fhir/messaging/quoted-message-id',
    QUOTED_SENDER: 'https://unify.health/fhir/messaging/quoted-sender',
    QUOTED_CONTENT: 'https://unify.health/fhir/messaging/quoted-content',
    QUOTED_IMAGE_URL: 'https://unify.health/fhir/messaging/quoted-image-url',
    HAS_UNREAD: 'https://unify.health/fhir/messaging/has-unread',
  },
  CATEGORY: {
    CLIENT: 'client-conversation',
    PROVIDER: 'provider-conversation',
    GROUP: 'space',
    SYSTEM: 'system',
  },
  /** Pre-PRD codes kept for backward compatibility with existing data. */
  LEGACY_CATEGORY: {
    CLIENT: 'client-thread',
    PROVIDER: 'provider-thread',
    GROUP: 'group-thread',
  },
  LEGACY_THREAD_IDENTIFIER_VALUE: 'thread-topic',
} as const;

// ── Group constants ───────────────────────────────────────────────────────────

export const MSG_GROUP = {
  /** Group.type for client conversations (Patient + Practitioner). */
  TYPE_CLIENT: 'person' as const,
  /** Group.type for provider conversations and spaces. */
  TYPE_PROVIDER: 'practitioner' as const,
  PRACTICE_TAG_SYSTEM: 'https://unify.health/fhir/practice-tag',
  SPACE_RULES_EXT: 'https://unify.health/fhir/messaging/space-rules',
  SPACE_ROLE_EXT: 'https://unify.health/fhir/messaging/space-role',
  MANUAL_ADD_EXT: 'https://unify.health/fhir/messaging/manual-add',
} as const;

/**
 * WebSocket subscription criteria for messaging Communications (PRD: practice tag when known).
 * Used where thread roots and messages may both need notification (e.g. space membership).
 */
export function messagingCommunicationSubscriptionCriteria(
  practiceTagCode?: string,
): string {
  if (practiceTagCode) {
    return `Communication?_tag=${MSG_GROUP.PRACTICE_TAG_SYSTEM}|${encodeURIComponent(practiceTagCode)}`;
  }
  return `Communication?identifier=${encodeURIComponent(MSG.IDENTIFIER_SYSTEM)}`;
}

/**
 * Broad per-tab subscription for new chat messages (nav badge + inbox sidebar).
 * Must target `identifier=…|message` — the pre-refactor criteria that reliably delivered
 * message creates. The generic `identifier=system` form does not match message events consistently.
 *
 * Outbound messages often lack `meta.tag`; do not AND with practice-tag here.
 */
export function broadMessagingSubscriptionCriteria(): string {
  return `Communication?identifier=${encodeURIComponent(MSG.IDENTIFIER_SYSTEM)}|${MSG.MESSAGE_IDENTIFIER_VALUE}`;
}

// ── Group helpers ─────────────────────────────────────────────────────────────

export interface CreateConversationGroupParams {
  type: 'person' | 'practitioner';
  members: Reference[];
  name?: string;
  managingEntity?: Reference;
  practiceTagCode?: string;
  spaceRules?: Array<{ type: string; value: string }>;
}

/** Create a FHIR Group resource to serve as the conversation membership container. */
export async function createConversationGroup(
  medplum: MedplumClient,
  params: CreateConversationGroupParams,
): Promise<FhirGroup> {
  const memberEntries: NonNullable<FhirGroup['member']> = params.members.map((ref) => ({
    entity: ref,
  })) as NonNullable<FhirGroup['member']>;

  const meta = params.practiceTagCode
    ? { tag: [{ system: MSG_GROUP.PRACTICE_TAG_SYSTEM, code: params.practiceTagCode }] }
    : undefined;

  const extensions = params.spaceRules?.length
    ? [
        {
          url: MSG_GROUP.SPACE_RULES_EXT,
          extension: params.spaceRules.map((rule) => ({
            url: 'rule',
            extension: [
              { url: 'type', valueCode: rule.type },
              { url: 'value', valueString: rule.value },
            ],
          })),
        },
      ]
    : undefined;

  return medplum.createResource<FhirGroup>({
    resourceType: 'Group',
    type: params.type,
    actual: true,
    active: true,
    ...(params.name ? { name: params.name } : {}),
    ...(params.managingEntity
      ? { managingEntity: params.managingEntity as FhirGroup['managingEntity'] }
      : {}),
    member: memberEntries,
    ...(meta ? { meta } : {}),
    ...(extensions ? { extension: extensions } : {}),
  } as FhirGroup);
}

/** Fetch Group resources where the user is a member (active conversations). */
export async function fetchConversationGroupsForUser(
  medplum: MedplumClient,
  userReference: string,
  practiceTagCode?: string,
): Promise<FhirGroup[]> {
  const params: Record<string, string> = {
    member: userReference,
    active: 'true',
    _count: '100',
  };
  if (practiceTagCode) {
    params._tag = `${MSG_GROUP.PRACTICE_TAG_SYSTEM}|${practiceTagCode}`;
  }
  return medplum.searchResources('Group', params);
}

/** Fetch the conversation header Communication for a given Group. */
export async function fetchConversationHeaderForGroup(
  medplum: MedplumClient,
  groupId: string,
): Promise<Communication | undefined> {
  for (const identifierValue of [MSG.THREAD_IDENTIFIER_VALUE, MSG.LEGACY_THREAD_IDENTIFIER_VALUE]) {
    const results = await medplum.searchResources('Communication', {
      subject: `Group/${groupId}`,
      identifier: `${MSG.IDENTIFIER_SYSTEM}|${identifierValue}`,
      _count: '1',
    });
    if (results[0]) return results[0];
  }
  return undefined;
}

// ── Queries ──────────────────────────────────────────────────────────────────

// ── Participant resolution ────────────────────────────────────────────────────

/** Reference strings for Patient or Practitioner (human participants, not Group). */
export function isDirectMessagingParticipantRef(ref: string | undefined): boolean {
  return ref != null && (ref.startsWith('Patient/') || ref.startsWith('Practitioner/'));
}

function isDirectParticipantRef(ref: string | undefined): boolean {
  return isDirectMessagingParticipantRef(ref);
}

/** Collect deduped Patient/Practitioner refs from a thread root (sender, recipient, subject). */
export function collectDirectParticipantRefsFromThread(thread: Communication): Reference[] {
  const seen = new Set<string>();
  const result: Reference[] = [];
  const add = (ref: Reference | undefined): void => {
    const r = ref?.reference;
    if (!r || !isDirectParticipantRef(r) || seen.has(r)) return;
    seen.add(r);
    result.push(ref as Reference);
  };
  add(thread.sender as Reference | undefined);
  for (const recipient of thread.recipient ?? []) {
    add(recipient);
  }
  const subjectRef = thread.subject?.reference;
  if (subjectRef && isDirectParticipantRef(subjectRef)) {
    add(thread.subject as Reference);
  }
  return result;
}

/**
 * Resolve the direct human participants of a thread.
 *
 * - If `threadRoot.recipient[]` contains Patient/Practitioner refs, returns those.
 * - If `threadRoot.subject.reference` is `Group/{id}`, reads the Group and returns
 *   `group.member[].entity` refs for Patient/Practitioner members.
 * - Deduplicates references. Excludes `Group/{id}` refs from the returned list.
 */
export async function resolveThreadParticipants(
  medplum: MedplumClient,
  threadRoot: Communication,
): Promise<Reference[]> {
  const seen = new Set<string>();
  const result: Reference[] = [];

  const add = (ref: Reference): void => {
    const r = ref.reference;
    if (!r || !isDirectParticipantRef(r) || seen.has(r)) return;
    seen.add(r);
    result.push(ref);
  };

  for (const r of threadRoot.recipient ?? []) {
    add(r);
  }

  if (threadRoot.sender) {
    add(threadRoot.sender as Reference);
  }

  const subjectRef = threadRoot.subject?.reference;
  if (subjectRef?.startsWith('Group/') && result.length === 0) {
    const groupId = subjectRef.replace('Group/', '');
    try {
      const group = await medplum.readResource('Group', groupId);
      for (const m of group.member ?? []) {
        if (m.entity) add(m.entity as Reference);
      }
    } catch {
      // Group read failed — fall through with whatever we have
    }
  }

  return result;
}

/**
 * Find the Patient reference among participants for `meta.account` tagging.
 *
 * Medplum's AccessPolicy `compartment: { reference: '%patient' }` causes
 * the server to gate subscription delivery and reads on `meta.account`.
 * Resources created by a Provider won't have the Patient's account unless
 * we set it explicitly. Without this, the Patient's WebSocket subscription
 * silently drops Communications created by other participants.
 */
function findPatientAccountRef(participants: Reference[]): Reference | undefined {
  return participants.find((r) => r.reference?.startsWith('Patient/'));
}

// ── Mutations ────────────────────────────────────────────────────────────────

/** Cast a generic Reference to the narrower Communication sender type. Safe when ref is Patient/Practitioner/etc. */
function asSender(ref: Reference): SenderRef {
  return ref as SenderRef;
}
function asRecipients(refs: Reference[]): RecipientRef[] {
  return refs as RecipientRef[];
}

// ── Quote / content helpers ───────────────────────────────────────────────────

// ── Attachments ──────────────────────────────────────────────────────────────

/** Parse per-user read timestamps from thread root extension (JSON map of reference → ISO time). */
export function parseReadByUserMap(comm: Communication): Record<string, string> {
  const raw = comm.extension?.find((e) => e.url === MSG.EXT.READ_BY_USER)?.valueString;
  if (!raw) return {};
  try {
    const o = JSON.parse(raw) as Record<string, string>;
    return o && typeof o === 'object' ? o : {};
  } catch {
    return {};
  }
}

/**
 * True if this participant has “seen” the latest thread activity (`thread.sent`), i.e. no unread dot.
 * Senders get their cursor bumped on send; others must open the thread (markThreadRootRead) or send.
 */
export function isParticipantCaughtUpWithThread(
  comm: Communication,
  currentUserReference: string | undefined,
): boolean {
  if (!currentUserReference) return true;

  const hasUnreadExt = comm.extension?.find(e => e.url === MSG.EXT.HAS_UNREAD);
  if (hasUnreadExt?.valueBoolean === true) return false;
  if (hasUnreadExt?.valueBoolean === false) return true;

  const lastActivity = comm.sent;
  if (!lastActivity) return true;

  const readMap = parseReadByUserMap(comm);
  const myRead = readMap[currentUserReference];

  if (!myRead) {
    const hasPerUserReadExtension =
      comm.extension?.some((e) => e.url === MSG.EXT.READ_BY_USER && e.valueString != null && e.valueString !== '') ??
      false;
    if (hasPerUserReadExtension) {
      // Thread uses per-user read map; missing entry means this participant has not read latest activity.
      return false;
    }
    const legacyGlobalRead =
      comm.extension?.some((ext) => ext.url === MSG.EXT.READ_AT && ext.valueDateTime) ?? false;
    return legacyGlobalRead;
  }

  return new Date(myRead).getTime() >= new Date(lastActivity).getTime();
}

/** Number of threads the viewer has not caught up with (sidebar unread dot). */
export function countUnreadThreadsForParticipant(
  threads: Communication[],
  currentUserReference: string | undefined,
): number {
  if (!currentUserReference) return 0;
  return threads.reduce(
    (n, t) => n + (isParticipantCaughtUpWithThread(t, currentUserReference) ? 0 : 1),
    0,
  );
}

/** Mock/local: merge sender read time onto thread root for sidebar parity. */
export function withBumpedSenderRead(
  thread: Communication,
  senderReference: string | undefined,
  activityTime: string,
  previewPayload?: Communication['payload'],
): Communication {
  if (!senderReference) return thread;
  const map = parseReadByUserMap(thread);
  map[senderReference] = activityTime;
  const ext = [...(thread.extension ?? [])];
  const iBy = ext.findIndex((e) => e.url === MSG.EXT.READ_BY_USER);
  const byUser = { url: MSG.EXT.READ_BY_USER, valueString: JSON.stringify(map) };
  if (iBy >= 0) ext[iBy] = byUser;
  else ext.push(byUser);
  return {
    ...thread,
    sent: activityTime,
    ...(previewPayload ? { payload: previewPayload } : {}),
    extension: ext,
  };
}

/** Local/server: set this participant’s read cursor to at least `thread.sent` (and current time when later). */
export function mergeReaderReadOnThread(
  thread: Communication,
  readerReference: string,
  readAt: string = new Date().toISOString(),
): Communication {
  const map = parseReadByUserMap(thread);
  const lastActivity = thread.sent;
  const t =
    lastActivity && new Date(readAt).getTime() < new Date(lastActivity).getTime()
      ? lastActivity
      : readAt;
  map[readerReference] = t;
  const ext = [...(thread.extension ?? [])];
  const iBy = ext.findIndex((e) => e.url === MSG.EXT.READ_BY_USER);
  const byUser = { url: MSG.EXT.READ_BY_USER, valueString: JSON.stringify(map) };
  if (iBy >= 0) ext[iBy] = byUser;
  else ext.push(byUser);
  return { ...thread, extension: ext };
}

/**
 * Persist “read” for this viewer on the thread Communication (`readByUser` JSON map).
 * Staff policies grant this Communication update. The patient policy does not.
 */
export async function markThreadRootRead(
  medplum: MedplumClient,
  threadId: string,
  readerReference: string,
): Promise<boolean> {
  try {
    const thread = (await medplum.readResource('Communication', threadId)) as Communication;
    const updated = mergeReaderReadOnThread(thread, readerReference);
    await medplum.updateResource(updated);
    return true;
  } catch (e) {
    console.warn('[Messaging] markThreadRootRead failed', e);
    return false;
  }
}

// ── Profile helpers ──────────────────────────────────────────────────────────

export function isThreadRoot(comm: Communication): boolean {
  return (
    comm.identifier?.some(
      (id) =>
        id.system === MSG.IDENTIFIER_SYSTEM &&
        (id.value === MSG.THREAD_IDENTIFIER_VALUE || id.value === MSG.LEGACY_THREAD_IDENTIFIER_VALUE),
    ) ?? false
  );
}

/** Find an existing client-conversation thread between a patient and a practitioner. */
export async function findClientThreadForPractitioner(
  medplum: MedplumClient,
  patientReference: string,
  practitionerId: string,
): Promise<Communication | undefined> {
  const practitionerRef = `Practitioner/${practitionerId}`;
  try {
    const groups = await fetchConversationGroupsForUser(medplum, patientReference);
    for (const group of groups) {
      if (!group.id) continue;
      const members = group.member ?? [];
      const hasPatient = members.some((m) => m.entity?.reference === patientReference);
      const hasPractitioner = members.some((m) => m.entity?.reference === practitionerRef);
      if (!hasPatient || !hasPractitioner) continue;

      const header = await fetchConversationHeaderForGroup(medplum, group.id);
      if (!header?.id) continue;

      const code = header.category?.[0]?.coding?.[0]?.code;
      if (code === MSG.CATEGORY.CLIENT || code === MSG.LEGACY_CATEGORY.CLIENT) {
        return header;
      }
    }
  } catch {
    return undefined;
  }
  return undefined;
}

/** Legacy thread roots without a Group subject (pre-migration or provider-initiated). */
async function findLegacyClientThreadBetweenPatientAndPractitioner(
  medplum: MedplumClient,
  patientReference: string,
  practitionerRef: string,
): Promise<Communication | undefined> {
  try {
    const candidates = await medplum.searchResources('Communication', {
      identifier: `${MSG.IDENTIFIER_SYSTEM}|${MSG.THREAD_IDENTIFIER_VALUE},${MSG.IDENTIFIER_SYSTEM}|${MSG.LEGACY_THREAD_IDENTIFIER_VALUE}`,
      recipient: patientReference,
      _count: '100',
    });

    for (const thread of candidates) {
      if (!isThreadRoot(thread)) continue;
      const code = thread.category?.[0]?.coding?.[0]?.code;
      if (code !== MSG.CATEGORY.CLIENT && code !== MSG.LEGACY_CATEGORY.CLIENT) continue;

      const refs = new Set(
        collectDirectParticipantRefsFromThread(thread)
          .map((r) => r.reference)
          .filter(Boolean) as string[],
      );
      if (refs.has(patientReference) && refs.has(practitionerRef)) {
        return thread;
      }
    }
  } catch {
    return undefined;
  }
  return undefined;
}

/**
 * Find an existing client↔provider thread (Group-based or legacy), reusing chat history
 * instead of creating a duplicate thread.
 */
export async function findClientThreadBetweenPatientAndPractitioner(
  medplum: MedplumClient,
  patientReference: string,
  practitionerId: string,
): Promise<Communication | undefined> {
  const fromGroup = await findClientThreadForPractitioner(medplum, patientReference, practitionerId);
  if (fromGroup?.id) return fromGroup;

  const practitionerRef = `Practitioner/${practitionerId}`;
  return findLegacyClientThreadBetweenPatientAndPractitioner(
    medplum,
    patientReference,
    practitionerRef,
  );
}

/** Create a client conversation with Group + thread root only (no first message). */
export async function createEmptyClientThread(
  medplum: MedplumClient,
  params: {
    senderRef: Reference;
    /** Other direct participant (typically Practitioner for client-initiated threads). */
    counterpartyRef?: Reference;
    /** @deprecated Use counterpartyRef — kept for backward compatibility. */
    patientRef?: Reference;
    practiceTagCode?: string;
  },
): Promise<Communication> {
  const counterpartyRef = params.counterpartyRef ?? params.patientRef;
  if (!counterpartyRef) {
    throw new Error('createEmptyClientThread requires counterpartyRef');
  }
  const { senderRef, practiceTagCode } = params;
  const now = new Date().toISOString();
  const allParticipants = [senderRef, counterpartyRef];

  const clientParticipant = allParticipants.find((r) => r.reference?.startsWith('Patient/'));
  const groupDisplay = clientParticipant?.display ?? counterpartyRef.display;

  const group = await createConversationGroup(medplum, {
    type: MSG_GROUP.TYPE_CLIENT,
    members: allParticipants,
    name: groupDisplay,
    practiceTagCode,
  });

  const groupRef: Reference = {
    reference: `Group/${group.id}`,
    display: groupDisplay,
  };

  const senderRefStr = senderRef.reference;
  const readByInitial =
    senderRefStr ? JSON.stringify({ [senderRefStr]: now }) : undefined;

  const patientAccountRef = findPatientAccountRef(allParticipants);
  const metaAccount = patientAccountRef?.reference
    ? { account: { reference: patientAccountRef.reference } }
    : undefined;
  const meta = {
    ...(practiceTagCode
      ? { tag: [{ system: MSG_GROUP.PRACTICE_TAG_SYSTEM, code: practiceTagCode }] }
      : {}),
    ...metaAccount,
  };
  const hasMeta = Object.keys(meta).length > 0;

  return medplum.createResource<Communication>({
    resourceType: 'Communication',
    ...(hasMeta ? { meta } : {}),
    identifier: [{ system: MSG.IDENTIFIER_SYSTEM, value: MSG.THREAD_IDENTIFIER_VALUE }],
    status: 'in-progress',
    category: [
      {
        coding: [
          {
            system: MSG.CATEGORY_SYSTEM,
            code: MSG.CATEGORY.CLIENT,
            display: 'Client Conversation',
          },
        ],
      },
    ],
    subject: groupRef as Communication['subject'],
    sender: asSender(senderRef),
    recipient: asRecipients([groupRef, ...allParticipants]),
    sent: now,
    ...(readByInitial
      ? { extension: [{ url: MSG.EXT.READ_BY_USER, valueString: readByInitial }] }
      : {}),
  });
}

// ── Backfill / repair ────────────────────────────────────────────────────────

/**
 * Idempotent repair: find Group-based thread roots whose `recipient` only contains
 * `Group/{id}` and patch them to `[Group/{id}, ...Group.member.entity]`.
 *
 * Safe to call on every startup or seed — skips threads that already include
 * direct participant refs.
 */
export async function repairGroupOnlyThreadRoots(
  medplum: MedplumClient,
): Promise<{ repaired: number; skipped: number }> {
  let repaired = 0;
  let skipped = 0;

  const threads = await medplum.searchResources('Communication', {
    identifier: `${MSG.IDENTIFIER_SYSTEM}|${MSG.THREAD_IDENTIFIER_VALUE},${MSG.IDENTIFIER_SYSTEM}|${MSG.LEGACY_THREAD_IDENTIFIER_VALUE}`,
    _count: '200',
  });

  for (const thread of threads) {
    const recipients = thread.recipient ?? [];
    const hasDirectParticipant = recipients.some(
      (r) => r.reference && isDirectParticipantRef(r.reference),
    );
    if (hasDirectParticipant) {
      skipped++;
      continue;
    }

    const subjectRef = thread.subject?.reference;
    if (!subjectRef?.startsWith('Group/')) {
      skipped++;
      continue;
    }

    try {
      const participants = await resolveThreadParticipants(medplum, thread);
      if (participants.length === 0) {
        skipped++;
        continue;
      }

      const groupRef = recipients.find((r) => r.reference?.startsWith('Group/'));
      const newRecipients = groupRef
        ? [groupRef, ...participants]
        : [...participants];

      await medplum.updateResource({
        ...thread,
        recipient: asRecipients(newRecipients),
      } as Communication);
      repaired++;
    } catch {
      skipped++;
    }
  }

  return { repaired, skipped };
}
